/* eslint-disable react-refresh/only-export-components */
/* eslint-disable react-hooks/set-state-in-effect */
/**
 * Layout страницы работы с задачей: блоки можно перетаскивать и менять размер.
 * Блоки: текст задачи, редактор кода, список задач, input, вывод программы.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactGridLayout, { useContainerWidth, verticalCompactor } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const LAYOUT_IDS = ['taskStatement', 'codeEditor', 'taskList', 'allTasks', 'outputBlock'];

/** Высота одной строки сетки (px). Одна строка ≈ высота заголовка блока при сворачивании. */
export const GRID_ROW_HEIGHT = 40;

const COLS = 12;

/** Пресет A: текст задачи сверху на всю ширину; ниже список/каталог/редактор; вывод под редактором. */
export const LAYOUT_PRESET_A = [
  { i: 'taskStatement', x: 0, y: 0, w: 12, h: 6, minW: 2, minH: 1 },
  { i: 'taskList', x: 0, y: 6, w: 3, h: 12, minW: 2, minH: 1 },
  { i: 'allTasks', x: 3, y: 6, w: 3, h: 12, minW: 2, minH: 1 },
  { i: 'codeEditor', x: 6, y: 6, w: 6, h: 12, minW: 4, minH: 2 },
  { i: 'outputBlock', x: 6, y: 18, w: 4, h: 6, minW: 2, minH: 1 },
];

/** Пресет B: слева текст/список/вывод, справа большой редактор + каталог под ним */
export const LAYOUT_PRESET_B = [
  { i: 'taskStatement', x: 0, y: 0, w: 4, h: 6, minW: 2, minH: 1 },
  { i: 'taskList', x: 0, y: 6, w: 4, h: 6, minW: 2, minH: 1 },
  { i: 'outputBlock', x: 0, y: 12, w: 4, h: 6, minW: 2, minH: 1 },
  { i: 'allTasks', x: 4, y: 12, w: 3, h: 9, minW: 2, minH: 1 },
  { i: 'codeEditor', x: 4, y: 0, w: 8, h: 12, minW: 4, minH: 2 },
];

/** Масштаб старых сохранённых раскладок (было rowHeight 120, стало GRID_ROW_HEIGHT 40). */
const LEGACY_LAYOUT_SCALE = 120 / GRID_ROW_HEIGHT;

/** Одна и та же ссылка, пока геометрия не изменилась — иначе RGL пересчитывает сетку на каждом рендере. */
function useStableLayout(layout) {
  const ref = useRef(layout);
  if (!layoutsEqual(ref.current, layout)) {
    ref.current = layout;
  }
  return ref.current;
}

export function layoutsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const byId = new Map(b.map((item) => [item.i, item]));
  for (const item of a) {
    const other = byId.get(item.i);
    if (!other) return false;
    if (
      item.x !== other.x ||
      item.y !== other.y ||
      item.w !== other.w ||
      item.h !== other.h
    ) {
      return false;
    }
  }
  return true;
}

export function loadLayoutFromStorage(storageKey) {
  try {
    const s = storageKey ? localStorage.getItem(storageKey) : null;
    if (s) {
      const parsed = JSON.parse(s);
      const layout = parsed?.layout;
      if (Array.isArray(layout) && layout.length === LAYOUT_IDS.length) {
        const maxH = Math.max(...layout.map((i) => i.h ?? 1));
        const isLegacy = maxH <= 8;
        return isLegacy
          ? layout.map((item) => ({
              ...item,
              y: Math.round((item.y ?? 0) * LEGACY_LAYOUT_SCALE),
              h: Math.max(1, Math.round((item.h ?? 1) * LEGACY_LAYOUT_SCALE)),
            }))
          : layout;
      }
    }
  } catch {
    // ignore
  }
  return LAYOUT_PRESET_A.map((item) => ({ ...item }));
}

export function saveLayoutToStorage(storageKey, layout, presetId = 'custom') {
  try {
    if (!storageKey) return;
    localStorage.setItem(storageKey, JSON.stringify({ layout, presetId }));
  } catch {
    // ignore
  }
}

function rectsOverlap(a, b) {
  return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

function absorbOmittedBlock(rest, at) {
  const y0 = at.y;
  const y1 = at.y + at.h;
  const x0 = at.x;
  const x1 = at.x + at.w;
  const rowsOverlap = (i) => i.y < y1 && i.y + i.h > y0;
  // Место уже занято видимым блоком (после перетаскивания) — повторный absorb
  // сдвигает соседей и при ресайзе «Вывода» редактор прыгает назад.
  if (rest.some((i) => rectsOverlap(i, at))) return;
  let grewRight = false;
  for (const i of rest) {
    if (!rowsOverlap(i)) continue;
    if (i.x === x1) {
      i.x = x0;
      i.w += at.w;
      grewRight = true;
    } else if (i.x > x1) {
      i.x -= at.w;
    }
  }
  if (grewRight) return;
  for (const i of rest) {
    if (!rowsOverlap(i)) continue;
    if (i.x + i.w === x0) {
      i.w += at.w;
    }
  }
}

/**
 * Убирает блоки из раскладки без absorb/compact — координаты видимых не трогаем (дыры ок).
 * Вне режима правки так стабильнее: иначе hide outputBlock → compact → скроллбар → петля.
 */
export function layoutOmitOnly(layout, omitIds) {
  const omit = new Set(omitIds);
  return layout.filter((i) => !omit.has(i.i)).map((i) => ({ ...i }));
}

/**
 * Убирает блоки из раскладки и отдаёт их место соседям на той же полосе, затем compact.
 */
export function layoutOmitAndAbsorb(layout, omitIds, cols = COLS) {
  const omit = new Set(omitIds);
  const rest = layout.filter((i) => !omit.has(i.i)).map((i) => ({ ...i }));
  for (const at of layout) {
    if (!omit.has(at.i)) continue;
    absorbOmittedBlock(rest, at);
  }
  return verticalCompactor.compact(rest, cols);
}

/** Игнорировать мелкие скачки ширины (скроллбар), иначе RGL пересчитывает сетку в цикле. */
const WIDTH_STABILITY_PX = 16;

function useStableContainerWidth() {
  const { width, containerRef, mounted } = useContainerWidth();
  const stableRef = useRef(0);
  if (width > 0 && (stableRef.current <= 0 || Math.abs(width - stableRef.current) >= WIDTH_STABILITY_PX)) {
    stableRef.current = width;
  }
  return { width: stableRef.current, containerRef, mounted };
}

/**
 * Убирает блок allTasks из раскладки сетки и отдаёт освободившуюся ширину соседу справа на той же полосе (preset A),
 * затем compact — чтобы не оставалось «дырок» (preset B и др.).
 */
export function layoutOmitAndAbsorbAllTasks(layout, cols = COLS) {
  return layoutOmitAndAbsorb(layout, ['allTasks'], cols);
}

/**
 * Сетка без скрытых блоков (каталог на уроке) живёт в «впитанных» координатах.
 * Если записать их поверх канонической раскладки, скрытый блок останется на старом месте,
 * и следующий absorb+compact откатит перетаскивание. Скрытые блоки паркуем под сеткой.
 */
export function mergeVisibleGridIntoCanonical(canonical, gridLayout, omitIds = []) {
  const omit = new Set(omitIds);
  const byId = Object.fromEntries((gridLayout || []).map((item) => [item.i, item]));
  const maxBottom = (gridLayout || []).reduce(
    (acc, item) => Math.max(acc, (item.y ?? 0) + (item.h ?? 1)),
    0,
  );
  let parkY = maxBottom;
  return (canonical || []).map((item) => {
    if (omit.has(item.i)) {
      const parked = { ...item, y: parkY };
      parkY += Math.max(1, item.h ?? 1);
      return parked;
    }
    const u = byId[item.i];
    return u ? { ...item, x: u.x, y: u.y, w: u.w, h: u.h } : item;
  });
}

export default function WorkPageBlocksLayout({
  taskStatement,
  taskStatementTitle,
  taskStatementSupplement,
  taskStatementHeaderActions,
  codeEditor,
  taskList,
  allTasks,
  allTasksTitle,
  allTasksHeaderActions,
  outputBlock,
  editMode = false,
  layoutStorageKey,
  layout,
  onLayoutChange: onLayoutChangeProp,
  taskStatementCollapsed = false,
  onTaskStatementCollapseToggle,
  onTaskStatementExpand,
  taskListCollapsed = false,
  onTaskListCollapseToggle,
  onTaskListExpand,
  allTasksCollapsed = false,
  onAllTasksCollapseToggle,
  onAllTasksExpand,
  /** false — не рендерить блок «Каталог задач» и выкинуть его из сетки (например, ДЗ от преподавателя). */
  showAllTasksBlock = true,
  /** false — спрятать редактор/черновик (тег «Тест», выбор, «собери программу»). Сохранённая раскладка не меняется. */
  showCodeWorkspace = true,
  /** false — спрятать вывод и кнопки запуска (граф/таблица без runner). */
  showRunner = true,
  workspaceTitle = 'Редактор кода',
  taskListHeaderActions,
  outputBlockRunInfo,
  codeEditorHeaderActions,
  className = '',
}) {
  const { width, containerRef, mounted } = useStableContainerWidth();
  const [internalLayout, setInternalLayout] = useState(() => loadLayoutFromStorage(layoutStorageKey));
  const isControlled = layout != null && typeof onLayoutChangeProp === 'function';
  const currentLayout = isControlled ? layout : internalLayout;
  const setLayout = isControlled ? onLayoutChangeProp : setInternalLayout;

  useEffect(() => {
    if (!isControlled && layoutStorageKey) {
      setInternalLayout(loadLayoutFromStorage(layoutStorageKey));
    }
  }, [layoutStorageKey]);

  const onLayoutChange = useCallback(
    (newLayout) => {
      const next = Array.isArray(newLayout) ? newLayout.map((item) => ({ ...item })) : newLayout;
      setLayout(next);
      if (!isControlled && layoutStorageKey) saveLayoutToStorage(layoutStorageKey, next, 'custom');
    },
    [setLayout, isControlled, layoutStorageKey],
  );

  const omittedIds = useMemo(() => {
    const ids = [];
    if (!showAllTasksBlock) ids.push('allTasks');
    if (!showCodeWorkspace) ids.push('codeEditor', 'outputBlock');
    else if (!showRunner) ids.push('outputBlock');
    return ids;
  }, [showAllTasksBlock, showCodeWorkspace, showRunner]);
  const gridEditMode = Boolean(editMode);

  // Вне ⊞ не компактим: иначе hide output / свёртка → сдвиг соседей → петля со скроллбаром.
  const anyCollapsed =
    taskStatementCollapsed || taskListCollapsed || (showAllTasksBlock && allTasksCollapsed);
  const displayLayout =
    anyCollapsed && gridEditMode
      ? verticalCompactor.compact(
          currentLayout.map((item) => ({ ...item })),
          COLS,
        )
      : currentLayout;

  const layoutForGridRaw = omittedIds.length
    ? (gridEditMode
        ? layoutOmitAndAbsorb(displayLayout, omittedIds, COLS)
        : layoutOmitOnly(displayLayout, omittedIds))
    : displayLayout;
  const layoutForGrid = useStableLayout(layoutForGridRaw);

  const onGridLayoutChange = useCallback(
    (newLayout) => {
      // Автокомпакт RGL при рендере/смене ширины не пишем в state — иначе блоки «ползут».
      if (!gridEditMode) return;
      const nextRaw = Array.isArray(newLayout) ? newLayout.map((item) => ({ ...item })) : newLayout;
      // Сравниваем с тем, что реально отдано в RGL (compact/omit), иначе петля при свёрнутых блоках.
      if (layoutsEqual(nextRaw, layoutForGrid)) return;
      const merged = mergeVisibleGridIntoCanonical(currentLayout, nextRaw, omittedIds);
      if (layoutsEqual(merged, currentLayout)) return;
      onLayoutChange(merged);
    },
    [gridEditMode, omittedIds, currentLayout, layoutForGrid, onLayoutChange],
  );

  return (
    <div ref={containerRef} className={`work-page-blocks-layout ${className}`}>
      {mounted && width > 0 && (
        <ReactGridLayout
          className="work-grid-layout"
          layout={layoutForGrid}
          onLayoutChange={onGridLayoutChange}
          width={width}
          gridConfig={{ cols: COLS, rowHeight: GRID_ROW_HEIGHT, margin: [6, 6], containerPadding: [6, 6] }}
          dragConfig={{ enabled: gridEditMode, handle: '.work-block-drag-handle' }}
          resizeConfig={{ enabled: gridEditMode, handles: ['s', 'e', 'w'] }}
        >
          <div key="taskStatement" className="work-block-outer">
            <div className="work-block">
              <div className="work-block-drag-handle work-block-title work-block-input-title">
                <div className="work-block-title-left">
                  <div className="work-block-title-cluster">
                    <span className="work-block-title-text-ellipsis">{taskStatementTitle ?? 'Текст задачи'}</span>
                    {taskStatementSupplement}
                  </div>
                  {taskStatementHeaderActions}
                </div>
                <div className="work-block-title-right">
                  {taskStatementCollapsed && typeof onTaskStatementExpand === 'function' && (
                    <button
                      type="button"
                      className="work-block-expand-btn"
                      onClick={(e) => { e.stopPropagation(); onTaskStatementExpand(); }}
                      title="Развернуть"
                      aria-label="Развернуть"
                    >
                      <span className="work-block-expand-icon" aria-hidden />
                    </button>
                  )}
                  {!taskStatementCollapsed && typeof onTaskStatementCollapseToggle === 'function' && (
                    <button
                      type="button"
                      className="work-block-collapse-btn"
                      onClick={(e) => { e.stopPropagation(); onTaskStatementCollapseToggle(); }}
                      title="Свернуть"
                      aria-label="Свернуть"
                    >
                      −
                    </button>
                  )}
                </div>
              </div>
              <div className="work-block-body work-block-statement">
                {taskStatementCollapsed ? (
                  <div className="collapsible-input-compact">
                    <span className="collapsible-input-filename">Текст задачи</span>
                  </div>
                ) : (
                  taskStatement
                )}
              </div>
            </div>
          </div>
          {showCodeWorkspace ? (
          <div key="codeEditor" className="work-block-outer">
            <div className="work-block">
              <div className="work-block-drag-handle work-block-title work-block-input-title">
                <div className="work-block-title-left">
                  <span>{workspaceTitle}</span>
                </div>
                <div className="work-block-title-right">
                  {codeEditorHeaderActions}
                </div>
              </div>
              <div className="work-block-body work-block-editor">{codeEditor}</div>
            </div>
          </div>
          ) : null}
          <div key="taskList" className="work-block-outer">
            <div className="work-block">
              <div className="work-block-drag-handle work-block-title work-block-input-title">
                <div className="work-block-title-left">
                  <span>Список задач</span>
                  {taskListHeaderActions}
                </div>
                <div className="work-block-title-right">
                  {taskListCollapsed && typeof onTaskListExpand === 'function' && (
                    <button
                      type="button"
                      className="work-block-expand-btn"
                      onClick={(e) => { e.stopPropagation(); onTaskListExpand(); }}
                      title="Развернуть"
                      aria-label="Развернуть"
                    >
                      <span className="work-block-expand-icon" aria-hidden />
                    </button>
                  )}
                  {!taskListCollapsed && typeof onTaskListCollapseToggle === 'function' && (
                    <button
                      type="button"
                      className="work-block-collapse-btn"
                      onClick={(e) => { e.stopPropagation(); onTaskListCollapseToggle(); }}
                      title="Свернуть"
                      aria-label="Свернуть"
                    >
                      −
                    </button>
                  )}
                </div>
              </div>
              <div className="work-block-body work-block-list">
                {taskListCollapsed ? (
                  <div className="collapsible-input-compact">
                    <span className="collapsible-input-filename">Список задач</span>
                  </div>
                ) : (
                  taskList
                )}
              </div>
            </div>
          </div>
          {showAllTasksBlock && (
            <div key="allTasks" className="work-block-outer">
              <div className="work-block">
                <div className="work-block-drag-handle work-block-title work-block-input-title">
                  <div className="work-block-title-left">
                    <span>{allTasksTitle ?? 'Каталог задач'}</span>
                    {allTasksHeaderActions}
                  </div>
                  <div className="work-block-title-right">
                    {allTasksCollapsed && typeof onAllTasksExpand === 'function' && (
                      <button
                        type="button"
                        className="work-block-expand-btn"
                        onClick={(e) => { e.stopPropagation(); onAllTasksExpand(); }}
                        title="Развернуть"
                        aria-label="Развернуть"
                      >
                        <span className="work-block-expand-icon" aria-hidden />
                      </button>
                    )}
                    {!allTasksCollapsed && typeof onAllTasksCollapseToggle === 'function' && (
                      <button
                        type="button"
                        className="work-block-collapse-btn"
                        onClick={(e) => { e.stopPropagation(); onAllTasksCollapseToggle(); }}
                        title="Свернуть"
                        aria-label="Свернуть"
                      >
                        −
                      </button>
                    )}
                  </div>
                </div>
                <div className="work-block-body work-block-list">
                  {allTasksCollapsed ? (
                    <div className="collapsible-input-compact">
                      <span className="collapsible-input-filename">{allTasksTitle ?? 'Каталог задач'}</span>
                    </div>
                  ) : (
                    allTasks
                  )}
                </div>
              </div>
            </div>
          )}
          {showCodeWorkspace && showRunner ? (
          <div key="outputBlock" className="work-block-outer">
            <div className="work-block">
              <div className="work-block-drag-handle work-block-title work-block-output-title">
                <div className="work-block-title-left">
                  <span>Вывод</span>
                </div>
                <div className="work-block-title-right">
                  {outputBlockRunInfo}
                </div>
              </div>
              <div className="work-block-body work-block-output">{outputBlock}</div>
            </div>
          </div>
          ) : null}
        </ReactGridLayout>
      )}
    </div>
  );
}
